<template>
    <div class="lamp-list-container">
        <!-- 顶部标题和搜索区域 -->
        <el-card shadow="never" class="header-card">
            <div class="header-content">
                <div class="header-title">
                    <h2>路灯信息记录</h2>
                </div>
                <div class="header-actions">
                    <el-date-picker v-model="selectedDate" type="date" placeholder="选择日期" value-format="YYYY-MM-DD"
                        @change="handleDateChange" clearable style="width: 200px;" />
                    <el-button type="primary" :icon="Refresh" @click="resetFilter" plain>
                        重置
                    </el-button>
                    <el-button type="success" :icon="Plus" @click="handleCreate" style="margin-left: 10px;">
                        新增记录
                    </el-button>
                </div>
            </div>
        </el-card>

        <!-- 数据展示区域 -->
        <el-card shadow="never" class="content-card">
            <div v-if="loading" class="loading-container">
                <el-icon class="loading-icon">
                    <Loading />
                </el-icon>
                <span>加载中...</span>
            </div>

            <div v-else-if="filteredGroupedData.length === 0" class="empty-container">
                <el-empty description="暂无路灯数据"
                    image="https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png" />
            </div>

            <div v-else class="lamp-group-container">
                <!-- 按日期分组展示 -->
                <div v-for="group in filteredGroupedData" :key="group.date" class="lamp-group">
                    <div class="group-header">
                        <el-tag type="info" size="large" effect="dark">
                            {{ formatDate(group.date) }}
                        </el-tag>
                        <span class="group-count">{{ group.items.length }} 条记录</span>
                    </div>

                    <el-table :data="group.items" border stripe style="width: 100%; margin-top: 12px"
                        :header-cell-style="{ background: '#f5f7fa' }" v-loading="group.loading">
                        <el-table-column label="时间段" width="220" align="center">
                            <template #default="{ row }">
                                <el-tag type="success" size="small">{{ row.lampparagraph.split('-')[0] }}</el-tag>
                                <span style="margin: 0 8px">至</span>
                                <el-tag type="info" size="small">{{ row.lampparagraph.split('-')[1] }}</el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column prop="lampcontent" label="内容" min-width="300" show-overflow-tooltip />

                        <el-table-column label="操作" width="180" align="center" fixed="right">
                            <template #default="scope">
                                <div class="action-buttons">
                                    <el-tooltip content="编辑记录" placement="top">
                                        <el-button type="primary" :icon="Edit" circle size="small"
                                            @click="handleEdit(scope.row)" />
                                    </el-tooltip>
                                    <el-tooltip content="删除记录" placement="top">
                                        <el-button type="danger" :icon="Delete" circle size="small"
                                            @click="handleDelete(scope.row)" />
                                    </el-tooltip>
                                </div>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>
            </div>
        </el-card>

        <!-- 编辑路灯记录对话框 -->
        <el-dialog v-model="editDialogVisible" title="编辑路灯记录" width="500px" center>
            <el-form ref="editFormRef" :model="editForm" :rules="editFormRules" label-width="100px">
                <el-form-item disabled label="日期" prop="lamptime">
                    <el-date-picker  disabled
                        v-model="editForm.lamptime" 
                        type="date" 
                        placeholder="选择日期" 
                        value-format="YYYY-MM-DD"
                    />
                </el-form-item>
                <el-form-item label="时间段" prop="lampparagraph">
                        <el-input disabled v-model="editForm.lampparagraph"/>
                </el-form-item>
                <el-form-item label="内容" prop="lampcontent">
                    <el-input v-model="editForm.lampcontent" type="textarea" rows="4" placeholder="请输入路灯相关内容" />
                </el-form-item>
            </el-form>
            <template #footer>
                <span class="dialog-footer">
                    <el-button @click="handleEditClose">取消</el-button>
                    <el-button type="primary" @click="handleEditSubmit">确认修改</el-button>
                </span>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue';
import {
    Refresh, Loading, Delete, Plus, Edit, Close
} from '@element-plus/icons-vue';
import axios from 'axios';
import { ElMessage, ElMessageBox, ElDialog, ElForm, ElFormItem, ElInput, ElSelect, ElOption } from 'element-plus';
import { useRouter } from 'vue-router';
const router = useRouter();

// 数据相关
const lampList = ref([]);
const loading = ref(false);
const selectedDate = ref('');
const isFirstLoad = ref(true);

// 编辑相关状态
const editDialogVisible = ref(false); // 编辑弹窗显示状态
const editForm = reactive({
    id: '',
    lamptime: '',
    lampcontent: '',
    lampparagraph: ''
});
const editFormRules = {
    lamptime: [{ required: true, message: '请选择日期', trigger: 'blur' }],
    lampcontent: [{ required: true, message: '请输入路灯内容', trigger: 'blur' }],
    lampparagraph: [{ required: true, message: '请选择时间段', trigger: 'blur' }]
};


// 获取路灯列表
const fetchLampList = async (params = {}) => {
    loading.value = true;
    try {
        const response = await axios.get('/adminapi/lamp/list', { params });
        // 注意：走到 else 就说明 response.data 是假值，不能再读它的 .message（会抛 TypeError）
        if (response.data) {
            lampList.value = response.data.results || [];
            // ElMessage.success('数据加载成功');
        } else {
            ElMessage.error('获取数据失败');
        }
    } catch (error) {
        console.error('获取路灯列表失败:', error);
        ElMessage.error('网络异常，请重试');
    } finally {
        loading.value = false;
    }
};

// 按日期分组并排序
const groupedData = computed(() => {
    const groups = {};

    // 按日期分组
    lampList.value.forEach(item => {
        if (!groups[item.lamptime]) {
            groups[item.lamptime] = [];
        }
        groups[item.lamptime].push(item);
    });

    // 将分组转换为数组并排序
    return Object.entries(groups)
        .map(([date, items]) => ({
            date,
            items: items.sort((a, b) => {
                // 提取时间段中的开始时间进行比较
                const getStartTime = timeStr => {
                    const [start] = timeStr.split('-');
                    const [h, m, s] = start.split(':').map(Number);
                    return h * 3600 + m * 60 + s;
                };
                return getStartTime(a.lampparagraph) - getStartTime(b.lampparagraph);
            })
        }))
        .sort((a, b) => new Date(b.date) - new Date(a.date)); // 日期从近到远排序
});

// 根据选择日期筛选数据
const filteredGroupedData = computed(() => {
    if (!selectedDate.value) {
        return groupedData.value;
    }
    return groupedData.value.filter(group => group.date === selectedDate.value);
});

// 格式化日期显示
const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long'
    });
};

// 日期选择变化处理
const handleDateChange = () => {
    if (selectedDate.value) {
        fetchLampList({ lamptime: selectedDate.value });
    } else {
        fetchLampList();
    }
};

// 重置筛选
const resetFilter = () => {
    selectedDate.value = '';
    fetchLampList();
};

// 删除记录
const handleDelete = async (id) => {
    try {
        const confirm = await ElMessageBox.confirm(
            '确定要删除这条路灯记录吗？此操作不可恢复。',
            '提示',
            {
                confirmButtonText: '确认删除',
                cancelButtonText: '取消',
                type: 'warning'
            }
        );
        const formData = reactive({
            lamptime: id.lamptime,
            lampcontent: id.lampcontent,
            lampparagraph: id.lampparagraph
        });

        if (confirm === 'confirm') {
            const response = await axios.post(`/adminapi/lamp/delete`, formData);
            if (response.data.code === 200) {
                ElMessage.success('删除成功');
                fetchLampList();
            } else {
                ElMessage.error(response.data.message || '删除失败');
            }
        }
    } catch (error) {
        // ElMessageBox 取消时 reject 的是字符串 'cancel'/'close'，接口报错是 Error 对象，
        // 原来一律提示「操作已取消」，会把真实的后端失败吞掉
        if (error === 'cancel' || error === 'close') {
            return;
        }
        console.error('删除路灯失败:', error);
        ElMessage.error(error?.response?.data?.message || '删除失败，请重试');
    }
};

// 编辑记录
const handleEdit = (row) => {
    // 填充编辑表单
    editForm.id = row.id || '';
    editForm.lamptime = row.lamptime;
    editForm.lampcontent = row.lampcontent;
    editForm.lampparagraph = row.lampparagraph;
    
    // 显示编辑弹窗
    editDialogVisible.value = true;
};

// 新增记录
const handleCreate = () => {
    router.push("/admin/lamp/add")
};

// 提交编辑表单
const handleEditSubmit = async () => {
    try {
        // 表单验证
        await editFormRef.value.validate();
        
        // 发送编辑请求到后端
        const response = await axios.post('/adminapi/lamp/update', editForm);
        if (response.data.code === 200) {
            ElMessage.success('编辑成功');
            editDialogVisible.value = false;
            fetchLampList();
        } else {
            ElMessage.error(response.data.message || '编辑失败');
        }
    } catch (error) {
        console.error('编辑失败:', error);
        ElMessage.error('编辑过程中出现错误，请重试');
    }
};

// 关闭编辑弹窗
const handleEditClose = () => {
    editDialogVisible.value = false;
    // 重置表单
    editForm.lamptime = '';
    editForm.lampcontent = '';
    editForm.lampparagraph = '';
    editFormRef.value.resetFields();
};

// 表单引用
const editFormRef = ref(null);

// 初始化加载数据
onMounted(() => {
    fetchLampList();
});

// 组件卸载时清理
onUnmounted(() => {
    // 可在此处添加清理逻辑
});
</script>

<style scoped lang="scss">
.lamp-list-container {
    padding: 20px;
    background-color: #f8fafc;
    min-height: calc(100vh - 60px);
}

.header-card {
    margin-bottom: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

    .header-content {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .header-title {
        h2 {
            margin: 0;
            font-size: 18px;
            font-weight: 600;
            color: #303133;
        }
    }

    .header-actions {
        display: flex;
        gap: 12px;
        align-items: center;
    }
}

.content-card {
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

    .loading-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 60px 0;

        .loading-icon {
            font-size: 40px;
            color: var(--el-color-primary);
            margin-bottom: 12px;
            animation: rotating 2s linear infinite;
        }

        span {
            color: #606266;
            font-size: 16px;
        }
    }

    .empty-container {
        padding: 60px 0;
        display: flex;
        justify-content: center;
    }
}

.lamp-group-container {
    .lamp-group {
        margin-bottom: 24px;
        background-color: #fff;
        border-radius: 6px;
        padding: 15px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

        &:last-child {
            margin-bottom: 0;
        }

        .group-header {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 12px;

            .group-count {
                font-size: 14px;
                color: #909399;
            }
        }
    }
}

.action-buttons {
    display: flex;
    gap: 6px;
}

@keyframes rotating {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

/* 响应式设计 */
@media (max-width: 768px) {
    .header-content {
        flex-direction: column;
        gap: 12px;
        align-items: flex-start !important;
    }

    .header-actions {
        width: 100%;
        flex-wrap: wrap;
    }

    .el-date-picker {
        width: 100% !important;
    }
}
/* 编辑对话框样式 */
.el-dialog {
    .el-form-item {
        margin-bottom: 20px;
    }
}
</style>